import { Injectable, inject } from '@angular/core'
import { Task } from '@awork/features/task/models/task.model'
import type { TaskStatus } from '@awork/features/task/models/task-status.model'
import { LogService } from '@awork/_shared/services/log-service/log.service'
import { Level } from '@awork/_shared/services/log-service/types'
import type { Project } from '@awork/features/project/models/project.model'
import { EntitySignalStore } from '@awork/core/state/signal-store/entitySignalStore'
import { WorkTypeStore } from '@awork/features/task/state/work-type.store'

@Injectable({ providedIn: 'root' })
export class TaskStore extends EntitySignalStore<Task> {
  private logService = inject(LogService)
  private workTypeStore = inject(WorkTypeStore)

  constructor() {
    super({
      name: 'task',
      entityConstructor: task => Task.mapTask(task),
      persistActiveState: false,
      nullableProps: [
        'projectId',
        'project',
        'parentId',
        'parentTask',
        'numberOfSubtasks',
        'primaryTaskListId',
        'closedOn',
        'createdFromTaskId',
        'subtaskOrder',
        'trackedDuration',
        'totalTrackedDuration',
        'totalPlannedDuration',
        'totalPlannedDurationWithHierarchy',
        'startOn',
        'dueOn'
      ],
      excludePersistProps: ['justCheckedOff']
    })
  }

  preUpdateEntity(prevEntity: Task, nextEntity: Partial<Task>): Partial<Task> {
    // to avoid saving nested structure in store
    const dueOn = nextEntity.dueOn || null
    const taskStatus = { ...nextEntity.taskStatus, tasks: undefined } as TaskStatus

    const modifiedOn = new Date().getTime()

    return { ...nextEntity, taskStatus, dueOn, modifiedOn }
  }

  preAddEntity(newEntity: Task): Task {
    const modifiedOn = new Date().getTime()

    return { ...newEntity, modifiedOn } as Task
  }

  /**
   * Resets justCheckedOff property for provided tasks in the store
   * @param {Task[]} checkedTasks
   */
  resetCheckedTasks(checkedTasks: Task[]): void {
    const checkedTasksIds = checkedTasks.map(task => task.id)

    if (checkedTasksIds.length) {
      this.update(checkedTasksIds, { justCheckedOff: undefined })
    }
  }

  /**
   * Synchronize a single task with the data fetched from the API
   * @param {Task} task
   */
  syncEntity(task: Task): void {
    super.syncEntity(task)

    // Get the worktype from the task to update the worktypes in the store
    if (task.project?.isExternal) {
      this.workTypeStore.syncWorkTypeFromTask(task)
    }
  }

  /**
   * Method to update the task (or tasks) in the store
   * @param {string | string[]} ids
   * @param {Partial<Task>} newState
   */
  updateTask(ids: string | string[], newState: Partial<Task>): void {
    // bump the store version depending on it is a list or just a task
    if (newState instanceof Task) {
      newState.storeVersion = newState.storeVersion ? newState.storeVersion + 1 : 1
    } else {
      newState = {
        ...newState,
        storeVersion: new Date().getTime()
      }
    }

    // regular update in the store
    try {
      this.update(ids, newState)
    } catch (error) {
      this.logService.logMessage(Level.error, 'Could not update task in store', { error })
    }
  }

  /**
   * Updates all the tasks of the project in the store and bumps the version
   * @param {Project} updatedProject the updated project
   */
  updateProjectsTasks(updatedProject: Project): void {
    if (updatedProject) {
      this.update(
        task => task.project?.id === updatedProject.id,
        ({ storeVersion }) => {
          return {
            project: updatedProject,
            storeVersion: (storeVersion || 0) + 1
          }
        }
      )
    }
  }
}
